import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Select, Space, DatePicker, Table, Alert, Tag, Tabs, Button,
  // recharts exports a Tooltip of its own further down, and it is the one
  // the charts use — this alias keeps both usable in the same file.
  Tooltip as AntTooltip,
} from 'antd';
import {
  DollarOutlined,
  WarningOutlined,
  StockOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  TeamOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import {
  ComposedChart, BarChart, Bar, Line, LineChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import AiAssistantTab from './AiAssistantTab';
import api from '../libs/api';

const { RangePicker } = DatePicker;

// Fiscal quarter helpers (FY Jul-Jun)
function getFiscalQuarter(d) {
  const m = d.month();
  if (m >= 6 && m <= 8) return { q: 1, start: d.year(), startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { q: 2, start: d.year(), startM: 9, endM: 11 };
  if (m >= 0 && m <= 2) return { q: 3, start: d.year(), startM: 0, endM: 2 };
  return { q: 4, start: d.year(), startM: 3, endM: 5 };
}

function getFiscalYearStart(d) {
  return d.month() >= 6
    ? dayjs().year(d.year()).month(6).startOf('month')
    : dayjs().year(d.year() - 1).month(6).startOf('month');
}

const now = dayjs();
const yesterday = now.subtract(1, 'day');
const curQ = getFiscalQuarter(now);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = getFiscalQuarter(prevQDate);
const fyStart = getFiscalYearStart(now);
const fyEnd = dayjs(fyStart).add(1, 'year').subtract(1, 'day').endOf('day');
const lastFyStart = fyStart.subtract(1, 'year');
const lastFyEnd = fyStart.subtract(1, 'day').endOf('day');

const DATE_PRESETS = [
  { label: 'Today', value: [now.startOf('day'), now.endOf('day')] },
  { label: 'Yesterday', value: [yesterday.startOf('day'), yesterday.endOf('day')] },
  { label: 'This Week', value: [now.startOf('week'), now.endOf('day')] },
  { label: 'Last Week', value: [now.subtract(1, 'week').startOf('week'), now.subtract(1, 'week').endOf('week')] },
  { label: 'This Month', value: [now.startOf('month'), now.endOf('month')] },
  { label: 'Last Month', value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().year(curQ.start).month(curQ.startM).startOf('month'), dayjs().year(curQ.start).month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [dayjs().year(prevQ.start).month(prevQ.startM).startOf('month'), dayjs().year(prevQ.start).month(prevQ.endM).endOf('month')] },
  { label: `This FY (${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, fyEnd] },
  { label: `Last FY (${lastFyStart.year()}/${lastFyStart.year() + 1})`, value: [lastFyStart, lastFyEnd] },
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(2)}`;
};

// Predefined NMI monitor sets (11-digit NMIs truncated to 10 digits, deduplicated)
const NMI_MONITOR_SETS = [
  {
    key: 'mitcham-batteries',
    name: 'Mitcham Batteries',
    nmis: [
      '2002560868', '2001371185', '2002588308', '2001359792', '2001364408',
      '2001368543', '2001062559', '2001346735', '2001365757', '2002113982',
      '2001360351', '2001758616', '2001359936', '2001345096', '2002236339',
      '2001359830', '2001371505', '2001370503', '2001372424', '2001363786',
      '2001359520', '2001063058', '2001059577', '2001371753', '2001065447',
      '2001363787', '2001058223', '2001061830', '2002574836', '2002329567',
      '2001714396', '2002271087', '2001360243', '2001662370',
    ],
  },
  {
    key: 'mitcham-street-light',
    name: 'Mitcham Street Light',
    nmis: [
      '2001809008', '2001808005',
    ],
  },
  {
    key: 'mitcham-nem13-meters',
    name: 'Mitcham Nem13 Meters',
    // = Mitcham EMC minus Street Light NMIs (2001809008, 2001808005). 133 NMIs.
    nmis: [
      '2001058223', '2001058725', '2001059105', '2001059577', '2001060047',
      '2001060080', '2001060191', '2001061789', '2001061820', '2001061830',
      '2001062555', '2001062557', '2001062559', '2001063017', '2001063058',
      '2001063059', '2001063704', '2001064632', '2001064662', '2001065447',
      '2001345042', '2001345096', '2001345345', '2001345700', '2001345891',
      '2001346710', '2001346735', '2001347157', '2001347441', '2001359520',
      '2001359792', '2001359830', '2001359934', '2001359936', '2001360243',
      '2001360351', '2001361611', '2001361694', '2001362472', '2001362944',
      '2001362984', '2001363144', '2001363528', '2001363786', '2001363787',
      '2001364408', '2001364683', '2001364687', '2001364994', '2001365355',
      '2001365376', '2001365757', '2001365947', '2001366379', '2001366413',
      '2001366559', '2001367017', '2001367786', '2001367928', '2001368026',
      '2001368082', '2001368104', '2001368336', '2001368459', '2001368460',
      '2001368543', '2001369451', '2001369588', '2001369589', '2001369591',
      '2001370503', '2001370800', '2001371185', '2001371330', '2001371437',
      '2001371504', '2001371505', '2001371753', '2001372424', '2001372789',
      '2001630366', '2001633431', '2001635792', '2001637539', '2001637556',
      '2001637567', '2001637806', '2001651730', '2001654639', '2001661648',
      '2001662370', '2001665265', '2001666300', '2001674773', '2001674882',
      '2001689147', '2001694431', '2001698302', '2001698314', '2001699742',
      '2001701459', '2001701654', '2001703194', '2001709057', '2001714396',
      '2001727970', '2001731532', '2001758568', '2001758616', '2002113982',
      '2002133639', '2002145695', '2002149634', '2002155960', '2002193102',
      '2002207536', '2002216054', '2002217363', '2002236339', '2002269438',
      '2002271087', '2002307166', '2002319864', '2002329329', '2002329567',
      '2002337068', '2002339403', '2002547726', '2002555832', '2002560868',
      '2002564353', '2002574836', '2002588308',
    ],
  },
  {
    key: 'mitcham-smart-meter',
    name: 'Mitcham Smart Meter',
    // 67 NMIs (11-digit source truncated to the 10-digit form used everywhere here).
    nmis: [
      '2001058223', '2001059105', '2001059577', '2001060191', '2001061789',
      '2001061830', '2001062557', '2001062559', '2001063058', '2001345096',
      '2001345700', '2001346710', '2001346735', '2001347441', '2001359520',
      '2001359792', '2001359830', '2001359936', '2001360243', '2001360351',
      '2001361611', '2001362472', '2001363528', '2001363786', '2001363787',
      '2001364408', '2001365757', '2001365947', '2001366413', '2001367017',
      '2001368543', '2001369451', '2001369588', '2001369589', '2001369591',
      '2001370503', '2001371185', '2001371330', '2001371505', '2001371753',
      '2001372424', '2001372789', '2001630366', '2001637539', '2001662370',
      '2001665265', '2001666300', '2001701459', '2001701654', '2001703194',
      '2001714396', '2001758616', '2002113982', '2002236339', '2002271087',
      '2002280024', '2002319864', '2002329329', '2002329567', '2002337068',
      '2002339403', '2002547726', '2002555832', '2002560868', '2002564353',
      '2002574836', '2002588308',
    ],
  },
  {
    key: 'mitcham-emc',
    name: 'Mitcham EMC',
    nmis: [
      '2001058223', '2001058725', '2001059105', '2001059577', '2001060047',
      '2001060080', '2001060191', '2001061789', '2001061820', '2001061830',
      '2001062555', '2001062557', '2001062559', '2001063017', '2001063058',
      '2001063059', '2001063704', '2001064632', '2001064662', '2001065447',
      '2001345042', '2001345096', '2001345345', '2001345700', '2001345891',
      '2001346710', '2001346735', '2001347157', '2001347441', '2001359520',
      '2001359792', '2001359830', '2001359934', '2001359936', '2001360243',
      '2001360351', '2001361611', '2001361694', '2001362472', '2001362944',
      '2001362984', '2001363144', '2001363528', '2001363786', '2001363787',
      '2001364408', '2001364683', '2001364687', '2001364994', '2001365355',
      '2001365376', '2001365757', '2001365947', '2001366379', '2001366413',
      '2001366559', '2001367017', '2001367786', '2001367928', '2001368026',
      '2001368082', '2001368104', '2001368336', '2001368459', '2001368460',
      '2001368543', '2001369451', '2001369588', '2001369589', '2001369591',
      '2001370503', '2001370800', '2001371185', '2001371330', '2001371437',
      '2001371504', '2001371505', '2001371753', '2001372424', '2001372789',
      '2001630366', '2001633431', '2001635792', '2001637539', '2001637556',
      '2001637567', '2001637806', '2001651730', '2001654639', '2001661648',
      '2001662370', '2001665265', '2001666300', '2001674773', '2001674882',
      '2001689147', '2001694431', '2001698302', '2001698314', '2001699742',
      '2001701459', '2001701654', '2001703194', '2001709057', '2001714396',
      '2001727970', '2001731532', '2001758568', '2001758616', '2001808005',
      '2001809008', '2002113982', '2002133639', '2002145695', '2002149634',
      '2002155960', '2002193102', '2002207536', '2002216054', '2002217363',
      '2002236339', '2002269438', '2002271087', '2002307166', '2002319864',
      '2002329329', '2002329567', '2002337068', '2002339403', '2002547726',
      '2002555832', '2002560868', '2002564353', '2002574836', '2002588308',
    ],
  },
];

// ============================================
// Tab 1: Connection Point Settlement
// ============================================
function CPTab() {
  const [dateRange, setDateRange] = useState([yesterday.startOf('day'), yesterday.endOf('day')]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [connPoints, setConnPoints] = useState([]);
  const [regionId, setRegionId] = useState(null);

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    setLoading(true);
    setApiError(null);
    try {
      const params = { start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') };
      if (connPoints.length > 0) params.nmi = connPoints.join(',');
      if (regionId) params.regionId = regionId;
      const res = await api.get('/api/power-tracking/summary', { params });
      setData(res.data.data);
    } catch (err) {
      setApiError(err.response?.data?.message || 'Failed to load data');
    }
    setLoading(false);
  }, [connPoints, regionId]);

  useEffect(() => { fetchData(dateRange); }, [dateRange, fetchData]);

  const stats = data?.stats || {};
  const dailyData = data?.dailyData || [];
  const cpBreakdown = data?.nmiBreakdown || [];
  const priceCurve = data?.priceCurve || [];
  const filterOptions = data?.filterOptions || {};

  const allCPs = filterOptions.connectionPoints || [];
  const filteredCPs = regionId ? allCPs.filter(c => c.region === regionId) : allCPs;
  const cpOptions = filteredCPs.map(c => ({ label: `${c.cp} (${c.region})`, value: c.cp }));
  const regionOptions = (filterOptions.regions || []).map(r => ({ label: r, value: r }));

  const handleRegionChange = (v) => {
    setRegionId(v || null);
    if (v) {
      setConnPoints(prev => prev.filter(cp => {
        const found = allCPs.find(c => c.cp === cp);
        return found && found.region === v;
      }));
    }
  };

  const handleCPChange = (values) => {
    setConnPoints(values || []);
    if (values && values.length > 0) {
      const regions = new Set(values.map(v => allCPs.find(c => c.cp === v)?.region).filter(Boolean));
      if (regions.size === 1) setRegionId([...regions][0]);
    }
  };

  const cpColumns = [
    { title: 'Connection Point', dataIndex: 'nmi', width: 140, fixed: 'left' },
    { title: 'Region', dataIndex: 'region', width: 80 },
    { title: 'Meter Type', dataIndex: 'meterType', width: 100 },
    { title: 'ASOE MWh', dataIndex: 'asoeMwh', width: 110, align: 'right', render: (v) => v?.toFixed(3), sorter: (a, b) => a.asoeMwh - b.asoeMwh },
    { title: 'ACE MWh', dataIndex: 'aceMwh', width: 110, align: 'right', render: (v) => v?.toFixed(3), sorter: (a, b) => a.aceMwh - b.aceMwh },
    { title: 'Total MWh', dataIndex: 'totalMwh', width: 110, align: 'right', render: (v) => v?.toFixed(3), sorter: (a, b) => a.totalMwh - b.totalMwh },
    { title: 'Settlement', dataIndex: 'totalAmount', width: 130, align: 'right', render: fmt, sorter: (a, b) => a.totalAmount - b.totalAmount, defaultSortOrder: 'descend' },
    { title: 'ACE Amount', dataIndex: 'aceAmount', width: 120, align: 'right', render: fmt },
    { title: 'Days', dataIndex: 'dayCount', width: 60, align: 'right' },
  ];

  return (
    <>
      {apiError && <Alert message={apiError} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setApiError(null)} />}

      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
          <span style={{ fontSize: 11, color: '#999' }}>AEST</span>
          <Select
            placeholder="Region"
            allowClear
            style={{ width: 110 }}
            size="small"
            value={regionId}
            onChange={handleRegionChange}
            options={regionOptions}
          />
          <Select
            mode="multiple"
            placeholder="Connection Point"
            allowClear
            showSearch
            style={{ minWidth: 200, maxWidth: 400 }}
            size="small"
            value={connPoints}
            onChange={handleCPChange}
            options={cpOptions}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            maxTagCount={3}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
          />
        </Space>
      </Card>

      <Spin spinning={loading}>
        {/* KPI Score Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card><Statistic title="Total Settlement Amount" value={stats.totalAmount} formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#05A677' }} />} valueStyle={{ color: '#05A677' }} /></Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card><Statistic title="Total Energy (MWh)" value={stats.totalMwh} precision={3} prefix={<ThunderboltOutlined style={{ color: '#54A2CC' }} />} /></Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card><Statistic title="Avg RRP ($/MWh)" value={stats.avgRRP} precision={2} prefix={<StockOutlined style={{ color: '#f5b759' }} />} valueStyle={{ color: '#f5b759' }} /></Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card><Statistic title="Active Connection Points" value={stats.nmiCount || 0} prefix={<TeamOutlined style={{ color: '#845EC2' }} />} /></Card>
          </Col>
        </Row>

        {/* Charts Row 1: Daily Settlement + ASOE/ACE/Settlement by CP */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Daily Settlement Amount" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} tick={{ fill: '#666' }} />
                  <YAxis tickFormatter={fmt} fontSize={11} />
                  <Tooltip formatter={(v) => [fmt(v), 'Amount']} />
                  <Bar dataKey="totalAmount" fill="#05A677" radius={[2, 2, 0, 0]} name="Settlement $" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="ASOE / ACE / Settlement by Connection Point" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={cpBreakdown.slice(0, 20)} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nmi" angle={-35} textAnchor="end" fontSize={9} interval={0} />
                  <YAxis yAxisId="mwh" fontSize={11} tickFormatter={(v) => `${v} MWh`} />
                  <YAxis yAxisId="amt" orientation="right" fontSize={11} tickFormatter={fmt} />
                  <Tooltip formatter={(v, name) => {
                    if (name === 'Settlement') return [fmt(v), name];
                    return [`${Number(v).toFixed(3)} MWh`, name];
                  }} />
                  <Legend />
                  <Bar yAxisId="mwh" dataKey="asoeMwh" fill="#05A677" fillOpacity={0.7} name="ASOE (Gen)" radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="mwh" dataKey="aceMwh" fill="#8B4513" fillOpacity={0.7} name="ACE (Con)" radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="amt" dataKey="totalAmount" fill="#54A2CC" fillOpacity={0.7} name="Settlement" radius={[2, 2, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Chart Row 2: ASOE/ACE/Settlement by Period — full width */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card title="ASOE / ACE / Settlement by 5-Min Period (avg)" size="small">
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={priceCurve} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" fontSize={10} interval={23} tick={{ fill: '#666' }} />
                  <YAxis yAxisId="mwh" fontSize={11} tickFormatter={(v) => `${v} MWh`} />
                  <YAxis yAxisId="amt" orientation="right" fontSize={11} tickFormatter={fmt} />
                  <Tooltip formatter={(v, name) => {
                    if (name === 'Settlement') return [fmt(v), name];
                    return [`${Number(v).toFixed(3)} MWh`, name];
                  }} />
                  <Legend />
                  <Bar yAxisId="mwh" dataKey="avgAsoe" fill="#05A677" fillOpacity={0.7} name="ASOE (Gen)" radius={[1, 1, 0, 0]} />
                  <Bar yAxisId="mwh" dataKey="avgAce" fill="#8B4513" fillOpacity={0.7} name="ACE (Con)" radius={[1, 1, 0, 0]} />
                  <Line yAxisId="amt" type="monotone" dataKey="avgAmount" stroke="#f5b759" strokeWidth={1.5} dot={false} name="Settlement" />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
}

// ============================================
// Tab 3: NMI Monitor Set
// ============================================
function MonitorSetTab({ onSelectSet }) {
  return (
    <Row gutter={[16, 16]}>
      {NMI_MONITOR_SETS.map(s => (
        <Col key={s.key} xs={24} sm={12} md={8} lg={6}>
          <Card
            hoverable
            size="small"
            onClick={() => onSelectSet(s.nmis)}
            style={{ cursor: 'pointer', borderColor: '#54A2CC' }}
          >
            <Statistic
              title={s.name}
              value={s.nmis.length}
              suffix="NMIs"
              prefix={<TeamOutlined style={{ color: '#54A2CC' }} />}
            />
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>Click to load in NMI Meter Data tab</div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

// ============================================
// Tab 2: NMI Meter Data
// ============================================
function NMITab({ monitorNmis, onMonitorApplied, onAnalyseWithAi }) {
  const [dateRange, setDateRange] = useState([yesterday.startOf('day'), yesterday.endOf('day')]);
  const [allNmis, setAllNmis] = useState([]);
  const [selectedNmis, setSelectedNmis] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [connPoints, setConnPoints] = useState([]);
  const [cpOptions, setCpOptions] = useState([]);
  const [focusedNmi, setFocusedNmi] = useState(null);
  const [focusedProfile, setFocusedProfile] = useState(null);
  const prevCPRef = useRef([]);
  // Aggregation mode for the data table + CSV download
  const [groupBy, setGroupBy] = useState('nmi');
  const [aggData, setAggData] = useState(null);
  const [aggLoading, setAggLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Apply monitor set NMIs from parent
  useEffect(() => {
    if (monitorNmis && monitorNmis.length > 0) {
      setSelectedNmis(monitorNmis);
      setConnPoints([]);
      prevCPRef.current = [];
      onMonitorApplied?.();
    }
  }, [monitorNmis, onMonitorApplied]);

  // Load NMI list + derive CP options
  useEffect(() => {
    api.get('/api/power-tracking/nmi-list').then(res => {
      const nmis = res.data.data || [];
      setAllNmis(nmis);
      // Derive CP options from NMI list
      const cpMap = {};
      nmis.forEach(n => { if (n.connectionPoint) cpMap[n.connectionPoint] = true; });
      setCpOptions(Object.keys(cpMap).sort().map(cp => ({ label: cp, value: cp })));
    }).catch(() => {});
  }, []);

  // When Connection Points change, auto-select their NMIs
  useEffect(() => {
    const prev = prevCPRef.current || [];
    const curr = connPoints;
    const added = curr.filter(cp => !prev.includes(cp));
    if (added.length > 0) {
      const newNmis = allNmis.filter(n => added.includes(n.connectionPoint)).map(n => n.nmi);
      setSelectedNmis(existing => {
        const set = new Set(existing);
        newNmis.forEach(n => set.add(n));
        return [...set];
      });
    }
    const removed = prev.filter(cp => !curr.includes(cp));
    if (removed.length > 0) {
      const removeNmis = new Set(allNmis.filter(n => removed.includes(n.connectionPoint)).map(n => n.nmi));
      setSelectedNmis(existing => existing.filter(n => !removeNmis.has(n)));
    }
    prevCPRef.current = [...curr];
  }, [connPoints, allNmis]);

  // Fetch NMI profile
  const fetchProfile = useCallback(async (range, nmis) => {
    if (!range || !range[0] || !range[1] || nmis.length === 0) {
      setProfileData(null);
      return;
    }
    setProfileLoading(true);
    try {
      const params = {
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD'),
        nmis: nmis.join(','),
      };
      const res = await api.get('/api/power-tracking/nmi-profile', { params });
      setProfileData(res.data.data);
    } catch (err) {
      console.error('NMI profile error:', err);
    }
    setProfileLoading(false);
  }, []);

  useEffect(() => { fetchProfile(dateRange, selectedNmis); setFocusedNmi(null); }, [dateRange, selectedNmis, fetchProfile]);

  // Fetch aggregated data for the table (mode-driven)
  useEffect(() => {
    if (!dateRange?.[0] || !dateRange?.[1] || selectedNmis.length === 0) { setAggData(null); return; }
    let cancelled = false;
    setAggLoading(true);
    api.get('/api/power-tracking/nmi-data', {
      params: {
        start: dateRange[0].format('YYYY-MM-DD'),
        end: dateRange[1].format('YYYY-MM-DD'),
        nmis: selectedNmis.join(','),
        groupBy,
      },
      timeout: 180_000,
    })
      .then(res => { if (!cancelled) setAggData(res.data.data); })
      .catch(err => { if (!cancelled) { console.error('NMI data error:', err); setAggData(null); } })
      .finally(() => { if (!cancelled) setAggLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange, selectedNmis, groupBy]);

  // Fetch profile for focused NMI
  useEffect(() => {
    if (!focusedNmi || !dateRange?.[0] || !dateRange?.[1]) { setFocusedProfile(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/power-tracking/nmi-profile', {
          params: { start: dateRange[0].format('YYYY-MM-DD'), end: dateRange[1].format('YYYY-MM-DD'), nmis: focusedNmi },
        });
        if (!cancelled) setFocusedProfile(res.data.data);
      } catch (e) { console.error('Focused NMI profile error:', e); }
    })();
    return () => { cancelled = true; };
  }, [focusedNmi, dateRange]);

  const activeProfile = focusedNmi ? focusedProfile : profileData;
  const meterProfile = activeProfile?.meterProfile || [];

  const nmiSelectOptions = allNmis.map(n => ({
    label: `${n.nmi} (${n.connectionPoint})`,
    value: n.nmi,
  }));

  const perNmi = profileData?.perNmi || {};
  const selectedNmiData = selectedNmis.map(nmi => {
    const info = allNmis.find(n => n.nmi === nmi) || {};
    const stats = perNmi[nmi] || {};
    return { nmi, connectionPoint: info.connectionPoint || '', tariff: info.tariff || '', ...stats };
  });

  const fmtMWh = v => v != null ? `${Number(v).toFixed(3)}` : '-';
  const fmtRev = v => v != null ? `$${Number(v).toFixed(2)}` : '-';

  // Column definitions per aggregation mode. Keys match the backend's
  // `columns` array so the table follows whatever shape came back.
  const AGG_COL_DEFS = {
    nmi: 'Per NMI (range totals)',
    interval: 'Per 5-min interval (all NMIs summed)',
    daily: 'Per day (all NMIs summed)',
    raw: 'Raw — one row per NMI × 5-min reading',
  };
  const COL_META = {
    nmi:             { title: 'NMI', width: 130 },
    connectionPoint: { title: 'Connection Point', width: 130 },
    tariff:          { title: 'Tariff', width: 90 },
    date:            { title: 'Date', width: 110, sorter: true },
    periodId:        { title: 'Period', width: 80, align: 'right', sorter: true },
    time:            { title: 'Time', width: 80 },
    exportMWh:       { title: 'Export B1 (MWh)', width: 130, align: 'right', render: fmtMWh, sorter: true },
    importMWh:       { title: 'Import E1 (MWh)', width: 130, align: 'right', render: fmtMWh, sorter: true },
    netMWh:          { title: 'Net (MWh)', width: 120, align: 'right', render: fmtMWh, sorter: true },
    exportKWh:       { title: 'Export B1 (kWh)', width: 130, align: 'right', render: fmtMWh, sorter: true },
    importKWh:       { title: 'Import E1 (kWh)', width: 130, align: 'right', render: fmtMWh, sorter: true },
    netKWh:          { title: 'Net (kWh)', width: 120, align: 'right', render: fmtMWh, sorter: true },
    rrp:             { title: 'RRP ($/MWh)', width: 120, align: 'right', render: fmtRev, sorter: true },
    avgRrp:          { title: 'Avg RRP ($/MWh)', width: 140, align: 'right', render: fmtRev, sorter: true },
    revenue: {
      title: 'Est. Revenue', width: 120, align: 'right', sorter: true,
      /**
       * Revenue values any interval with no spot price at zero, which reads as
       * "earned nothing" rather than "priced nothing". When the price feed has
       * a hole the figure is understated and nothing on screen says so — which
       * is how a fortnight of readings came to show $0.00 — so an incomplete
       * figure is marked where it is shown.
       */
      render: (v, row) => {
        const missing = Number(row?.unpricedReadings) || 0;
        if (!missing) return fmtRev(v);
        const total = Number(row?.readings) || 0;
        return (
          <AntTooltip title={`${missing}${total ? ` of ${total}` : ''} intervals have no spot price, `
            + 'and count as zero here — the real figure is larger'}
          >
            <span style={{ borderBottom: '1px dotted #d48806' }}>
              {fmtRev(v)} <WarningOutlined style={{ color: '#d48806' }} />
            </span>
          </AntTooltip>
        );
      },
    },
    nmiCount:        { title: 'NMIs', width: 80, align: 'right', sorter: true },
    readings:        { title: 'Readings', width: 100, align: 'right', sorter: true },
  };
  const aggColumns = (aggData?.columns || []).map(key => {
    const meta = COL_META[key] || { title: key, width: 120 };
    const col = { title: meta.title, dataIndex: key, width: meta.width, align: meta.align, render: meta.render };
    if (meta.sorter) {
      col.sorter = (a, b) => {
        const av = a[key], bv = b[key];
        return typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''));
      };
    }
    return col;
  });
  // Only the per-NMI view gets per-row actions: in the other aggregations a row
  // covers many NMIs, so neither "remove this NMI" nor "analyse this NMI" has a
  // single subject.
  if (groupBy === 'nmi') {
    aggColumns.push({
      title: '', width: 150, align: 'center', fixed: 'right',
      render: (_, row) => (
        <Space size={10}>
          {/* native title, not Tooltip: in this file Tooltip is recharts' */}
          <a
            title="Open this NMI in the AI Assistant"
            onClick={() => onAnalyseWithAi && onAnalyseWithAi(row.nmi)}
            style={{ fontSize: 12 }}
          >
            <RobotOutlined style={{ marginRight: 3 }} />AI analyse
          </a>
          <a onClick={() => setSelectedNmis(prev => prev.filter(n => n !== row.nmi))} style={{ color: '#f5222d', fontSize: 12 }}>
            Remove
          </a>
        </Space>
      ),
    });
  }
  const aggRows = (aggData?.rows || []).map((r, i) => ({ ...r, _k: `${groupBy}-${i}` }));

  return (
    <>
      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
          <span style={{ fontSize: 11, color: '#999' }}>AEST</span>
          <Select
            mode="multiple"
            placeholder="Connection Point"
            allowClear
            showSearch
            style={{ minWidth: 200, maxWidth: 400 }}
            size="small"
            value={connPoints}
            onChange={setConnPoints}
            options={cpOptions}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            maxTagCount={3}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
          />
        </Space>
      </Card>

      {/* NMI Selector + Table */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle" wrap={false}>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Select
              mode="multiple"
              placeholder="Search and select NMIs..."
              showSearch
              style={{ width: '100%' }}
              size="small"
              value={selectedNmis}
              onChange={setSelectedNmis}
              options={nmiSelectOptions}
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
              maxTagCount={4}
              maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            />
          </Col>
          <Col flex="none">
            <a onClick={() => setSelectedNmis(allNmis.map(n => n.nmi))} style={{ fontSize: 12, marginRight: 8 }}>Select all</a>
            <span style={{ fontSize: 12, color: '#888', marginRight: 8 }}>{selectedNmis.length} selected</span>
            <Select
              size="small"
              value={groupBy}
              onChange={setGroupBy}
              style={{ width: 190, marginRight: 8 }}
              options={[
                { label: 'Aggregate: by NMI',      value: 'nmi' },
                { label: 'Aggregate: 5-min',       value: 'interval' },
                { label: 'Aggregate: daily',       value: 'daily' },
                { label: 'No aggregation (raw)',   value: 'raw' },
              ]}
            />
            <Button
              type="primary"
              size="small"
              loading={downloading}
              disabled={selectedNmis.length === 0 || !dateRange?.[0] || !dateRange?.[1]}
              style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
              onClick={async () => {
                setDownloading(true);
                try {
                  const params = {
                    start: dateRange[0].format('YYYY-MM-DD'),
                    end: dateRange[1].format('YYYY-MM-DD'),
                    nmis: selectedNmis.join(','),
                    groupBy,
                    format: 'csv',
                  };
                  const res = await api.get('/api/power-tracking/nmi-data', {
                    params, responseType: 'blob', timeout: 300_000,
                  });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `nmi-${groupBy}-${params.start}-to-${params.end}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (err) {
                  // surface failure quietly — backend logs the details
                  console.error('NMI CSV download failed:', err);
                }
                setDownloading(false);
              }}
            >
              Download CSV
            </Button>
          </Col>
        </Row>
        {selectedNmis.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 8 }}>
              {[...new Set(selectedNmiData.map(n => n.connectionPoint))].filter(Boolean).map(cp => (
                <Tag key={cp} color="blue" style={{ marginBottom: 4 }}>{cp}: {selectedNmiData.filter(n => n.connectionPoint === cp).length} NMIs</Tag>
              ))}
              <a onClick={() => setSelectedNmis([])} style={{ fontSize: 12, marginLeft: 8 }}>Clear all</a>
              <span style={{ fontSize: 11, color: '#999', marginLeft: 12 }}>
                {AGG_COL_DEFS[groupBy]}
                {aggData?.rows ? ` · ${aggData.rows.length.toLocaleString()} rows` : ''}
              </span>
            </div>
            {aggData?.truncated && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
                message={`Preview capped at ${aggData.rowCap.toLocaleString()} rows. Download CSV for the full dataset.`}
              />
            )}
            <Spin spinning={aggLoading}>
              <Table
                dataSource={aggRows}
                rowKey="_k"
                size="small"
                pagination={aggRows.length > 20 ? { defaultPageSize: 20, size: 'small', showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] } : false}
                columns={aggColumns}
                scroll={{ x: 'max-content' }}
                rowClassName={(row) => (groupBy === 'nmi' && row.nmi === focusedNmi) ? 'ant-table-row-selected' : ''}
                onRow={(row) => ({
                  onClick: (e) => {
                    if (groupBy !== 'nmi') return;
                    if (e.target.closest('a')) return;
                    setFocusedNmi(prev => prev === row.nmi ? null : row.nmi);
                  },
                  style: { cursor: groupBy === 'nmi' ? 'pointer' : 'default' },
                })}
                onHeaderRow={() => ({
                  onClick: () => { if (groupBy === 'nmi') setFocusedNmi(null); },
                  style: { cursor: groupBy === 'nmi' ? 'pointer' : 'default' },
                })}
              />
            </Spin>
          </div>
        )}
      </Card>

      {/* NMI KPI Cards */}
      {selectedNmis.length > 0 && (profileData?.meterProfile?.length > 0) && (() => {
        const nmiStats = focusedNmi ? perNmi[focusedNmi] : null;
        const t = nmiStats || profileData?.totals || {};
        const totalExport = t.totalExportMWh || 0;
        const totalImport = t.totalImportMWh || 0;
        const netConsumption = t.netMWh != null ? t.netMWh : (totalImport - totalExport);
        const totalRevenue = t.revenue != null ? t.revenue : (t.totalRevenue || 0);
        const cardLabel = focusedNmi ? ` — NMI ${focusedNmi}` : '';
        return (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={12} md={6}>
              <Card><Statistic title={`Total Generation (MWh)${cardLabel}`} value={totalExport} precision={3} prefix={<ThunderboltOutlined style={{ color: '#05A677' }} />} valueStyle={{ color: '#05A677' }} /></Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card><Statistic title={`Total Consumption (MWh)${cardLabel}`} value={totalImport} precision={3} prefix={<ThunderboltOutlined style={{ color: '#8B4513' }} />} valueStyle={{ color: '#8B4513' }} /></Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card><Statistic title={`Net Consumption (MWh)${cardLabel}`} value={netConsumption} precision={3} prefix={<StockOutlined style={{ color: netConsumption > 0 ? '#f5222d' : '#05A677' }} />} valueStyle={{ color: netConsumption > 0 ? '#f5222d' : '#05A677' }} /></Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card><Statistic title={`Est. Total Revenue${cardLabel}`} value={totalRevenue} formatter={(v) => fmt(v)} prefix={<DollarOutlined style={{ color: '#54A2CC' }} />} valueStyle={{ color: '#54A2CC' }} /></Card>
            </Col>
          </Row>
        );
      })()}

      {/* NMI Profile Charts */}
      <Spin spinning={profileLoading}>
        {selectedNmis.length > 0 && meterProfile.length > 0 && (
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card
                title="Import / Export Profile + Spot Price (avg per period)"
                size="small"
                extra={<span style={{ fontSize: 11, color: '#999' }}>NEM12 (5-min interval) only — NEM13 cumulative reads not included</span>}
              >
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={meterProfile} syncId="nmiProfile" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" fontSize={10} interval={23} tick={{ fill: '#666' }} />
                    <YAxis yAxisId="kwh" fontSize={11} tickFormatter={(v) => `${v} kWh`} />
                    <YAxis yAxisId="price" orientation="right" fontSize={11} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v, name) => {
                      if (name === 'Spot Price') return [`$${Number(v).toFixed(2)}/MWh`, name];
                      return [`${Number(v).toFixed(3)} kWh`, name];
                    }} />
                    <Legend />
                    <Bar yAxisId="kwh" dataKey="avgExport" fill="#05A677" fillOpacity={0.7} name="Export (B1)" radius={[1, 1, 0, 0]} />
                    <Bar yAxisId="kwh" dataKey="avgImport" fill="#8B4513" fillOpacity={0.7} name="Import (E1)" radius={[1, 1, 0, 0]} />
                    <Line yAxisId="price" type="monotone" dataKey="avgRRP" stroke="#f5b759" strokeWidth={1.5} dot={false} name="Spot Price" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24}>
              <Card title="Estimated Settlement (B1−E1) × Spot Price (avg per period)" size="small">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={meterProfile} syncId="nmiProfile" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" fontSize={10} interval={23} tick={{ fill: '#666' }} />
                    <YAxis tickFormatter={fmt} fontSize={11} />
                    <Tooltip formatter={(v) => [fmt(v), 'Est. Settlement']} />
                    <Bar dataKey="estAmount" fill="#54A2CC" radius={[2, 2, 0, 0]} name="Est. Settlement $" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        )}
      </Spin>
    </>
  );
}

// ============================================
// Tab 4: AEMO Prudential Outstandings
// ============================================
function PrudentialTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/power-tracking/prudential')
      .then(res => setData(res.data.data || []))
      .catch(err => setError(err.response?.data?.message || 'Failed to load prudential data'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Spin spinning={loading}>
      {error && <Alert message={error} type="warning" showIcon style={{ marginBottom: 16 }} />}
      <Card title="Powow Power (POWOW) — AEMO Prudential Outstandings" size="small">
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data || []} margin={{ top: 5, right: 20, bottom: 40, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" angle={-30} textAnchor="end" fontSize={10} />
            <YAxis tickFormatter={fmt} fontSize={11} />
            <Tooltip formatter={(v, name) => [fmt(v), name]} />
            <Legend />
            <Line type="monotone" dataKey="outstandings" stroke="#05A677" strokeWidth={2} dot={false} name="Outstandings" />
            <Line type="monotone" dataKey="tradingLimit" stroke="#FA5252" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="Trading Limit" />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </Spin>
  );
}

// ============================================
// Main Page
// ============================================
export default function PowerTracking() {
  // The settlement tabs and the assistant are separately permissioned: some
  // roles have the assistant only. Read straight from the granted permissions —
  // no role-type shortcut, so nothing here can hand out access the backend would
  // refuse.
  const { canSettlement, canAi } = useMemo(() => {
    let perms = [];
    try { perms = JSON.parse(localStorage.getItem('permissions') || '[]'); } catch { perms = []; }
    return {
      canSettlement: perms.includes('page:power-tracking'),
      // page:power-tracking counts too. The permission list in localStorage is
      // written at login, so everyone already signed in when page:power-ai was
      // created has a stale copy without it — which hid the tab from people who
      // do hold the permission in the database. Migration 018 granted power-ai
      // to every power-tracking holder, so accepting either is not a loosening:
      // the backend still checks page:power-ai and will find it.
      canAi: perms.includes('page:power-ai') || perms.includes('page:power-tracking'),
    };
  }, []);

  const [activeTab, setActiveTab] = useState(canSettlement ? 'cp' : 'ai');
  const [monitorNmis, setMonitorNmis] = useState(null);

  const handleSelectSet = useCallback((nmis) => {
    setMonitorNmis(nmis);
    setActiveTab('nmi');
  }, []);

  // A NMI handed over from the meter table. Kept in the parent so the assistant
  // tab picks it up on mount even though the tabs render lazily.
  const [aiSn, setAiSn] = useState(null);
  const handleAnalyseWithAi = (nmi) => {
    if (!nmi) return;
    setAiSn(String(nmi));
    setActiveTab('ai');
  };

  const handleMonitorApplied = useCallback(() => {
    setMonitorNmis(null);
  }, []);

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Powow Power Tracking</h1>
        <p>
          {canSettlement
            ? 'Settlement data and energy transactions (SQL Server — PowowPowerDI)'
            : 'Battery strategy and bill analysis for a single system'}
        </p>
      </div>

      {/* Tabs the caller cannot use are not rendered at all rather than disabled.
          antd mounts a tab's children on first activation, so leaving them in
          would fire settlement requests that the backend answers with 403. */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          ...(canSettlement ? [
            { key: 'cp', label: 'Connection Point Settlement', children: <CPTab /> },
            { key: 'nmi', label: 'NMI Meter Data', children: <NMITab monitorNmis={monitorNmis} onMonitorApplied={handleMonitorApplied} onAnalyseWithAi={handleAnalyseWithAi} /> },
            { key: 'monitor', label: 'NMI Monitor Set', children: <MonitorSetTab onSelectSet={handleSelectSet} /> },
            { key: 'prudential', label: 'Prudential', children: <PrudentialTab /> },
          ] : []),
          ...(canAi ? [
            { key: 'ai', label: 'AI Assistant', children: <AiAssistantTab initialNmi={aiSn} /> },
          ] : []),
        ]}
      />
    </PageLayout>
  );
}
